import React, { useState } from 'react';
import { ClipboardList, ChevronDown, ChevronUp } from 'lucide-react';

// Staff notes: emoji/short/colon lines are headings, quoted or ? lines are questions, Yes/No/→ lines are follow-ups
const classify = (raw) => {
  const line = raw.replace(/^[-•*]\s*/, '');
  const noEmoji = line.replace(/^[\p{Extended_Pictographic}\uFE0F\u200D\s]+/u, '').trim();
  if (noEmoji !== line || /:$/.test(line) || (line.length <= 40 && !/[.?!"”]$/.test(line) && !/^(→|->|yes|no)\b/i.test(line))) return ['h', noEmoji.replace(/:$/, '')];
  if (/^["“]/.test(line) || /\?["”]?$/.test(line)) return ['q', line.replace(/^["“]|["”]$/g, '')];
  if (/^(→|->|yes\b|no\b|\+)/i.test(line)) return ['a', line.replace(/^(->)/, '→')];
  return ['t', line];
};

// Shown on the POS when a scanned product belongs to a protocol: what to say about it + what to ask first
export const ProtocolTillGuide = ({ condition, scanned, staffNotes }) => {
  const [open, setOpen] = useState(true);
  const lines = (staffNotes || '').split('\n').map(l => l.trim()).filter(Boolean);
  if (!scanned && !lines.length) return null;
  const slug = condition.replace(/[^a-z0-9]+/gi, '-').toLowerCase();

  return (
    <div className="mt-3 rounded-lg border border-teal-300 bg-white/80 overflow-hidden" data-testid={`till-guide-${slug}`}>
      {scanned && (scanned.benefit || scanned.caution || scanned.dose) && (
        <div className="px-3 py-2 border-b border-teal-100" data-testid={`till-guide-scanned-${slug}`}>
          <p className="text-[11px] font-bold uppercase tracking-wide text-teal-700">What to tell the customer · {scanned.term}</p>
          {scanned.benefit && <p className="text-sm text-slate-800 mt-0.5">{scanned.benefit}</p>}
          {scanned.dose && <p className="text-sm text-indigo-700 mt-0.5"><span className="font-semibold">Dose:</span> {scanned.dose}</p>}
          {scanned.caution && <p className="text-sm text-amber-800 font-medium mt-0.5">⚠ {scanned.caution}</p>}
        </div>
      )}
      {lines.length > 0 && (
        <div className="bg-rose-50">
          <button
            onClick={() => setOpen(o => !o)}
            className="w-full flex items-center justify-between px-3 py-2 text-left"
            data-testid={`till-guide-toggle-${slug}`}
          >
            <span className="text-[11px] font-bold uppercase tracking-wide text-rose-800 flex items-center gap-1.5">
              <ClipboardList className="w-3.5 h-3.5" /> Ask before recommending — {condition} screening
            </span>
            {open ? <ChevronUp className="w-4 h-4 text-rose-700" /> : <ChevronDown className="w-4 h-4 text-rose-700" />}
          </button>
          {open && (
            <div className="px-3 pb-3 space-y-0.5" data-testid={`till-guide-notes-${slug}`}>
              {lines.map((raw, i) => {
                const [kind, text] = classify(raw);
                if (kind === 'h') return <p key={i} className="text-xs font-bold uppercase tracking-wide text-rose-900 pt-2 first:pt-0">{text}</p>;
                if (kind === 'q') return <p key={i} className="text-sm text-slate-900 pl-4 relative"><span className="absolute left-0 font-bold text-teal-700">?</span>{text}</p>;
                if (kind === 'a') return <p key={i} className="text-xs text-rose-900 pl-8">{text}</p>;
                return <p key={i} className="text-sm text-slate-800 pl-4 relative"><span className="absolute left-1 text-rose-600">•</span>{text}</p>;
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
